/**
 * Courts to check (Dani, 26 Oct 2026: "I don't have time to go in and manually fix every single
 * court that's in there with the wrong information").
 *
 * Not every court: only the ones with open cases, busiest first, each with what is wrong. A court
 * leaves the list when someone marks its details correct, so the work shrinks instead of being
 * redone every time somebody trips over a bad record.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ManagerApi, { CourtToCheckDto } from '../../api/ManagerApi';
import { courtTypeLabel } from '../../utils/courtType';

const ManagerCourtsToCheckView: React.FC = () => {
  const [rows, setRows] = useState<CourtToCheckDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showVerified, setShowVerified] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await ManagerApi.courtsToCheck(showVerified));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the courts');
    }
  }, [showVerified]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  const markCorrect = async (row: CourtToCheckDto) => {
    setSaving(row.Court_guid);
    try {
      await ManagerApi.verifyCourt(row.Court_guid, true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the check');
    } finally {
      setSaving(null);
    }
  };

  const held = rows?.filter((r) => r.problems.some((p) => p.blocksLor)).length ?? 0;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Courts to check
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Only courts with open cases, busiest first. Open a court, fix what is listed, then press
        "Details are correct" and it leaves this list. Courts marked in red have their letters held at
        LOR@ until their email is checked.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {rows && (
          <Typography variant="body2">
            <strong>{rows.length}</strong> court{rows.length === 1 ? '' : 's'} to check
            {held > 0 ? `, ${held} with letters held` : ''}.
          </Typography>
        )}
        <FormControlLabel
          control={<Switch checked={showVerified} onChange={(e) => setShowVerified(e.target.checked)} />}
          label="Include courts already checked"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!rows && !error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows && rows.length === 0 ? (
        <Alert severity="success">Every court with an open case has been checked.</Alert>
      ) : rows ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Court</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Open cases</TableCell>
                <TableCell>What to fix</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const blocks = row.problems.some((p) => p.blocksLor);
                return (
                  <TableRow key={row.Court_guid} sx={blocks ? { backgroundColor: 'rgba(211, 47, 47, 0.06)' } : undefined}>
                    <TableCell>
                      <Link to={`/court?Court_guid=${row.Court_guid}`}>{row.Court_Name || '(no name)'}</Link>
                      {row.Court_County && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {row.Court_County} County
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{courtTypeLabel(row.Court_Type)}</TableCell>
                    <TableCell align="right">{row.openCases}</TableCell>
                    <TableCell>
                      {row.problems.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Nothing found. Confirm the details are right.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {row.problems.map((p) => (
                            <Box key={p.code}>
                              <Chip
                                size="small"
                                color={p.blocksLor ? 'error' : 'warning'}
                                variant={p.blocksLor ? 'filled' : 'outlined'}
                                label={p.message}
                                sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" component={Link} to={`/court?Court_guid=${row.Court_guid}`}>
                        Open
                      </Button>
                      {!row.Court_VerifiedAt && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => markCorrect(row)}
                          disabled={saving === row.Court_guid}
                        >
                          Details are correct
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Container>
  );
};

export default ManagerCourtsToCheckView;
